/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Dec 21 2023
 **/

import { EventHandler } from "@rfice/eventhandler";
import { RestAPIError } from "@/frameworks/libraries/restservice";

import { EventID } from "@/frameworks/core/commons";
import { useChat } from "@/frameworks/core/API";
import useOpenWorldChatActions from "./useOpenWorldChatActions";

export default function useSpaceChatRoomActions() {
	const { space } = useChat();
	const openworld = useOpenWorldChatActions();

	/**
	 * 채팅방 생성
	 */
	const create = async ({
		userIDs,
		spaceID,
		name,
		isPrivate
	}: {
		userIDs: string[];
		spaceID: string;
		name: string;
		isPrivate: boolean;
	}) => {
		const result = await space.create.execute({
			userIds: userIDs,
			spaceId: spaceID,
			name,
			private: isPrivate
		});

		return result;
	};

	/**
	 * 채팅방에 유저 추가
	 */
	const addUsers = async (
		args: WebServices.SpaceChat.AddSpaceChatUser.Request & {
			events?: {
				onSuccess?: (users: WebServices.SpaceChat.AddSpaceChatUser.Request["userIds"]) => void;
				onError?: (error: RestAPIError) => void;
			};
		}
	) => {
		space.addUsers
			.execute({
				roomId: args.roomId,
				userIds: args.userIds,
				spaceId: args.spaceId
			})
			.then(() => {
				args.events?.onSuccess?.(args.userIds);
			});
	};

	/**
	 * 채널 삭제
	 */
	const remove = async (
		spaceID: string,
		roomID: string,
		events?: {
			onSuccess?: (roomID: string) => void;
			onError?: (error: RestAPIError) => void;
		}
	) => {
		space.delete
			.execute({
				roomId: roomID,
				spaceId: spaceID
			})
			.then(() => {
				events?.onSuccess?.(roomID);
				EventHandler.emit(EventID.onDeleteRoom, {
					type: "deleteChannel",
					id: roomID
				});
			})
			.catch((err) => {
				events?.onError?.(err);
			});
	};

	/**
	 * 채팅방 나가기
	 */
	const exit = async (
		roomID: string,
		deviceKey: string,
		events?: {
			onSuccess?: (roomID: string) => void;
			onError?: (error: RestAPIError) => void;
		}
	) => {
		space.leave
			.execute({ roomId: roomID, deviceKey: deviceKey })
			.then(() => {
				events?.onSuccess?.(roomID);
				EventHandler.emit(EventID.onExitRoom, {
					type: "leaveChannel",
					id: roomID
				});
			})
			.catch((err) => {
				events?.onError?.(err);
			});
	};

	/**
	 * 채팅방 이름 수정
	 */
	const rename = (
		args: WebServices.SpaceChat.Update.Request & {
			events?: {
				onSuccess?: () => void;
				onError?: (error: RestAPIError) => void;
			};
		}
	) => {
		space.update
			.execute({
				spaceId: args.spaceId,
				roomId: args.roomId,
				name: args.name
			})
			.then(() => {
				args.events?.onSuccess?.();
			});
	};

	return {
		create,
		addUsers,
		remove,
		exit,
		rename,
		openworld
	};
}
